// libs
import styled from "styled-components";
import {
  color,
  ColorProps,
  layout,
  LayoutProps,
  space,
  SpaceProps,
  typography,
  TypographyProps,
} from "styled-system";

export type TextVariant =
  | "display"
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "body"
  | "small";

interface TextProps
  extends SpaceProps,
    TypographyProps,
    LayoutProps,
    ColorProps {
  variant?: TextVariant;
  as?: string | React.ComponentType<any>;
}

const StyledText = styled.p<Omit<TextProps, "variant">>`
  ${space}
  ${typography}
  ${color}
  ${layout}
`;

export const Text: React.FC<TextProps> = ({
  variant,
  children,
  as,
  ...rest
}) => {
  const variantMap: { [key: string]: JSX.Element } = {
    display: (
      <StyledText
        fontSize={[12, null, null, null, 15, 16, 17]}
        {...rest}
        as={as || "h1"}
      >
        {children}
      </StyledText>
    ),
    h1: (
      <StyledText
        fontSize={[9, null, null, null, 13, 15]}
        fontWeight="700"
        as={as || "h1"}
        {...rest}
      >
        {children}
      </StyledText>
    ),
    h2: (
      <StyledText
        fontSize={[8, null, null, null, 11, 14]}
        fontWeight="700"
        as={as || "h2"}
        {...rest}
      >
        {children}
      </StyledText>
    ),
    h3: (
      <StyledText
        fontSize={[6, null, null, null, 8, 10]}
        fontWeight="700"
        as={as || "h3"}
        {...rest}
      >
        {children}
      </StyledText>
    ),
    h4: (
      <StyledText
        fontSize={[5, null, null, null, 6, 7]}
        fontWeight="700"
        as={as || "h4"}
        {...rest}
      >
        {children}
      </StyledText>
    ),
    h5: (
      <StyledText
        fontSize={[2, null, null, null, 3, 6]}
        fontWeight="700"
        as={as || "h5"}
        {...rest}
      >
        {children}
      </StyledText>
    ),
    body: (
      <StyledText
        fontSize={[2, null, null, null, null, 3, 5]}
        as={as || "p"}
        {...rest}
      >
        {children}
      </StyledText>
    ),
    small: (
      <StyledText fontSize={[0]} {...rest} as={as || "p"} {...rest}>
        {children}
      </StyledText>
    ),
  };

  return variant ? variantMap[variant] : variantMap.body;
};
